import React, { FC, useEffect, useState } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

import { styleSizesNumber } from 'constants/styleSizes';
import StyledButton from 'components/Button';
import { useLoadTrips } from 'customHooks/useLoadTrips';
import { smoothNavigate } from 'components/SmoothNavigate';

import { BillingOverAllContentContainer } from './style';
import { DownloadIcon } from 'assets/icons';

let debug = false;

const billingStatuses: any = {
  PENDING: {
    text: 'Upcoming Trip',
    className: 'confirmed',
  },
  CONFIRMED: {
    text: 'Upcoming Trip',
    className: 'confirmed',
  },
  INCOMPLETE: {
    text: 'Upcoming Trip',
    className: 'confirmed',
  },
  CHECKED_IN: {
    text: 'Checked in',
    className: 'checkedIn',
  },
  CHECKED_OUT: {
    text: 'Paid',
    className: 'checkedOut',
  },
  PROCESSED: {
    text: 'Paid',
    className: 'checkedOut',
  },
  CANCELLED: {
    text: 'Canceled',
    className: 'cancelled',
  },
  CANCELED: {
    text: 'Canceled',
    className: 'cancelled',
  },
  EXPIRED: {
    text: 'Expired',
    className: 'cancelled',
  },
  REFUNDED: {
    text: 'Refunded',
    className: 'cancelled',
  },
};

const BillingOverAllContent: FC = () => {
  const history = useHistory();
  const { trips, loading, paymentCards } = useLoadTrips();


  const [isTablet, setIsTablet] = useState(
    window.innerWidth <= styleSizesNumber.tabletBreakpoint
  );

  const isRefunded = (group: any) => {
    return group.bills?.some((bill: any) => {
      return bill.transactions?.some((transaction: any) => {
        return (
          transaction?.type === 'REFUND' ||
          transaction?.isRefunded === true
        );
      });
    });
  };




  const getGroupBillingStatus = (bookings: any[] = []) => {
    if (bookings.length === 0) {
      return 'PENDING';
    }

    let hasCheckedIn = false;

    let checkedOutStatusCounter = 0;
    let processedStatusCounter = 0;
    let expiredStatusCounter = 0;
    let cancelledStatusCounter = 0;
    let incompleteBookings = 0;

    bookings.forEach((booking: any) => {
      switch (booking.status) {
        case 'PROCESSED':
          processedStatusCounter++;
          break;

        case 'CHECKED_OUT':
          checkedOutStatusCounter++;
          break;

        case 'CHECKED_IN':
          hasCheckedIn = true;
          break;

        case 'EXPIRED':
          expiredStatusCounter++;
          break;

        case 'CANCELLED':
        case 'CANCELED':
          cancelledStatusCounter++;
          break;

        case 'PENDING':
          if (!(booking.fullName && booking.phoneNumber)) {
            incompleteBookings++;
          }
          break;

        default:
          break;
      }
    });

    if (processedStatusCounter === bookings.length) {
      return 'PROCESSED';
    }

    if (checkedOutStatusCounter === bookings.length) {
      return 'PROCESSED';
    }

    if (expiredStatusCounter === bookings.length) {
      return 'EXPIRED';
    }

    if (cancelledStatusCounter === bookings.length) {
      return 'CANCELLED';
    }

    if (
      cancelledStatusCounter + expiredStatusCounter === bookings.length &&
      cancelledStatusCounter > 0 &&
      expiredStatusCounter > 0
    ) {
      return 'EXPIRED';
    }

    if (hasCheckedIn) {
      return 'CHECKED_IN';
    }

    if (incompleteBookings > 0) {
      return 'INCOMPLETE';
    }

    return 'PENDING';
  };

  useEffect(() => {
    if (debug) {
      console.log('[billingOverAll]trips:', trips);
    }
  }, [trips]);

  useEffect(() => {
    if (debug) {
      console.log('[billingOverAll]paymentCards:', paymentCards);
    }
  }, [paymentCards]);


  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth <= styleSizesNumber.tabletBreakpoint);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getBillingStatus = (group: any) => {
    if (isRefunded(group)) {
      return 'REFUNDED';
    }

    return getGroupBillingStatus(group.bookings);
  };

  const canModifyCard = (status: string) => {
    return (
      status === 'PENDING' ||
      status === 'CONFIRMED' ||
      status === 'INCOMPLETE' ||
      status === 'CHECKED_IN'
    );
  };


  const getPaymentCardByGroupId = (groupId: string) => {
    return (
      paymentCards.find((card: any) => String(card.groupId) === String(groupId) && card.isPrimary) ||
      paymentCards.find((card: any) => String(card.groupId) === String(groupId))
    );
  };

  const getMaskedCardNumberByGroupId = (groupId: string) => {
    const card = getPaymentCardByGroupId(groupId);

    if (!card?.transaction?.creditCardNumber) {
      return 'No card';
    }

    const cardNumber = String(card.transaction.creditCardNumber).replace(/\s/g, '');
    const lastFourDigits = cardNumber.slice(-4);

    return `**** **** ${lastFourDigits}`;
  };

  const billingRows = Object.keys(trips).map((groupId, index) => {
    const group = trips[groupId];

    if (group.bookings.length === 0) {
      return null;
    }

    const billingStatus = getBillingStatus(group);
    const statusData = billingStatuses[billingStatus] || billingStatuses.PENDING;
    const showModify = canModifyCard(billingStatus);

    if (debug) {
      console.log('[billingOverAll]row status:', {
        groupId,
        primaryBookingCode: group.primaryBookingCode,
        billingStatus,
        bookings: group.bookings,
        bills: group.bills,
      });
    }

    const amount = (group.bills || [])
      .reduce((acc: number, bill: any) => acc + (bill.total || 0), 0)
      .toFixed(2);

      const firstBooking = group.bookings[0];

      const checkInDate = firstBooking?.checkInDate
        ? moment(firstBooking.checkInDate).format('DD MMM, h:mm A')
        : '-';
      
      const checkOutDate = firstBooking?.checkOutDate
        ? moment(firstBooking.checkOutDate).format('DD MMM, h:mm A')
        : '-';


    return (
      <div key={index} className="trip">
        <div className={`status ${statusData.className}`}>
          {statusData.text}
          {isTablet ? <div className='primaryBookingCodeTablet'>{group.primaryBookingCode}</div> : ''}
        </div>

        <div className="reservationCode">
          <span className="mobileHdr">CONF #</span>
          {group.primaryBookingCode}
        </div>

        <div className="dates">
          {moment(group.bookings[0]?.checkInDate).format('MMM Do YYYY')}
        </div>

        <div className="datesMobile">
          <div className="datesMobileRow">
            <div className="datesMobileLabel">CHECK-IN</div>
            <div className="datesMobileValue">{checkInDate}</div>
          </div>

          <div className="datesMobileRow">
            <div className="datesMobileLabel">CHECK-OUT</div>
            <div className="datesMobileValue">{checkOutDate}</div>
          </div>
        </div>

        <div className="total">
          <span className="mobileHdr">TOTAL</span>
          <div
            className="modifyCardLink"
            onClick={() => smoothNavigate(history, `/account/bookings/${groupId}`)}
          >
            <div className='modifyCardLinkAmount'>$ {amount}</div>
            Details
          </div>
        </div>

        <div className="card">
          <span className="mobileHdr">CARD</span>
          <div className="cardData">
            {showModify ? (
              <div
                className="modifyCardLink"
                onClick={() => smoothNavigate(history, `/account/billing/credit-card-details/${groupId}`)}
              >
                {getMaskedCardNumberByGroupId(groupId)}
              </div>
            ) : 
            (
              <div className='cardNoLink'>{getMaskedCardNumberByGroupId(groupId)}</div>
            )
            }

            {showModify && (
              <div
                className="modifyCardLink"
                onClick={() => smoothNavigate(history, `/account/billing/credit-card-details/${groupId}`)}
              >
                <div className='modifyCardLinkModify'>Update Card</div>
              </div>
            )}

          </div>

        </div>

        <div className="button">
          <StyledButton
            width={showModify ? "96px" : "123px"}
            height="48px"
            startIcon={showModify ? undefined : <DownloadIcon />}
            onClick={() => {
              if (showModify) {
                smoothNavigate(history, `/account/bookings/${groupId}`);
                return;
              }

              window.open(group.receipt, '_blank');
            }}
          >
            {showModify ? 'Edit Trip' : 'Receipt'}
          </StyledButton>

        </div>
      </div>
    );
  });

  return (
    <BillingOverAllContentContainer>
      <div className="accountBody">
        {(Object.keys(trips).length === 0) && !loading ? (
          <div className="noBooking">
            Looks like there aren't any bookings here.
          </div>
        ) : (
          <div className="cardContent">
            <div className="bookingTableHeader">
              <div className="status">{isTablet ? 'STATUS/ID' : 'STATUS'}</div>
              <div className="reservationCode">BOOKING ID</div>
              <div className="dates">DATE</div>
              <div className="total">AMOUNT</div>
              <div className="card">CARD</div>
              <div className='button'>&nbsp;</div>
            </div>

            <div className="bookingTableContent">
              {billingRows}
            </div>
          </div>
        )}
      </div>
    </BillingOverAllContentContainer>
  );
};

export default BillingOverAllContent;